import { Serwist } from '@serwist/window';

export interface RegisterServiceWorkerOptions {
  /**
   * SW 的對外路徑。與各 app serwist 設定的 swDest 檔名一致，預設 `/sw.js`。
   *
   * 多 app 共用網域時要放進自己的 base path（`/diet/sw.js`）——**SW 的控制範圍
   * 不能超過它自己所在的目錄**，這是瀏覽器的硬規則。放在根目錄的 SW 會攔截整個
   * 網域，包含別的 app。
   */
  swUrl?: string;
  /**
   * SW 的控制範圍。預設由 `swUrl` 的所在目錄推導，通常不需要指定。
   *
   * 不可超出 `swUrl` 的目錄，否則註冊會被瀏覽器拒絕（SecurityError）。
   */
  scope?: string;
  /**
   * 設為 true 時跳過註冊。開發環境應傳入 true——dev server 不產生 `sw.js`，
   * 且快取會干擾熱更新。
   */
  disabled?: boolean;
}

/**
 * 註冊 PWA 的 Service Worker（框架無關）。
 *
 * 直接呼叫的形式，供各 app 的 `main.tsx` 使用。底層是 serwist。
 *
 * 註冊失敗只記錄不拋出：SW 是漸進增強，拿不到離線能力不該讓整個 app 開不起來。
 *
 * @example
 * // apps/<app>/src/main.tsx
 * registerServiceWorker({ disabled: !import.meta.env.PROD });
 */
export function registerServiceWorker({
  swUrl = '/sw.js',
  scope,
  disabled = false,
}: RegisterServiceWorkerOptions = {}): void {
  if (disabled) return;

  /* 靜態產生階段沒有 navigator；不支援的瀏覽器也沒有 serviceWorker */
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  const serwist = new Serwist(swUrl, { scope: scope ?? directoryOf(swUrl) });

  void serwist.register().catch((error: unknown) => {
    console.error('Service Worker 註冊失敗，本次將以純線上模式運作。', error);
  });
}

/**
 * 取 SW 檔案所在的目錄，作為預設 scope。
 *
 * `/sw.js` → `/`；`/diet/sw.js` → `/diet/`。這正好是瀏覽器允許的最大範圍，
 * 也是多 app 共用網域時各自該圈的疆界。
 */
function directoryOf(swUrl: string): string {
  return swUrl.slice(0, swUrl.lastIndexOf('/') + 1);
}
